"use client";

import React from "react";
import { BrainCircuit, Dna, Microscope } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  title: "Genomic Sequencing",
  description: "We analyze specific genetic markers to identify the root-cause of issues, minimizing trial-and-error in psychopharmacology.",
  icon: Dna
}, {
  title: "Neural Pathway Mapping",
  description: "Understanding your unique neurobiology allows us to target specific receptor sites for more effective symptom relief.",
  icon: BrainCircuit
}, {
  title: "Metabolic Optimization",
  description: "Adjusting dosage based on your liver enzyme activity ensures optimal therapeutic levels - with minimal side effects.",
  icon: Microscope
}];
export default function TheScienceOfTargetedTreatment() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-science-of-targeted-treatment" className="dark bg-background text-foreground relative overflow-hidden py-24">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.03]">
        <svg viewBox="0 0 200 600" className="h-[150%] w-auto animate-[spin_60s_linear_infinite] text-primary" aria-hidden="true">
          <path fill="currentColor" d="M45.7,0C43.6,8.6,35.8,16.3,28.2,24.1c-7.6,7.8-15.1,15.8-19.8,25.5c-4.7,9.7-6.1,21.1-2.8,31.5
            c3.3,10.4,11.4,19.1,20.8,25.5c9.4,6.4,20.1,10.5,31.1,12.8c11,2.3,22.4,2.8,33.2,0.9c10.8-1.9,21-6.3,29.6-12.8
            c8.6-6.5,15.6-15.1,19.8-25.1c4.2-10,5.5-21.2,3.2-31.9c-2.3-10.7-8.2-20.3-16.5-27.8C118.5,14.9,107.9,9.6,97,6.4
            C86.1,3.2,74.8,2.1,63.8,3.2C52.8,4.3,42.2,7.6,32.9,13.1c-9.3,5.5-17.3,13.2-22.8,22.5c-5.5,9.3-8.3,20.1-8.3,30.9
            c0,10.8,2.8,21.6,8.3,30.9c5.5,9.3,13.5,17,22.8,22.5c9.3,5.5,19.9,8.8,30.9,9.9c11,1.1,22.3,0,33.2-3.2
            c10.9-3.2,21.5-8.5,29.8-15.9c8.3-7.4,14.2-17,16.5-27.8c2.3-10.7,1-21.9-3.2-31.9c-4.2-10-11.2-18.6-19.8-25.1
            c-8.6-6.5-18.8-10.9-29.6-12.8c-10.8-1.9-22.2-1.4-33.2,0.9c-11,2.3-21.7,6.4-31.1,12.8c-9.4,6.4-17.5,15.1-20.8,25.5
            c-3.3,10.4-1.9,21.8,2.8,31.5c4.7,9.7,12.2,17.7,19.8,25.5c7.6,7.8,15.4,15.5,17.5,24.1" />
          {}
          <path fill="currentColor" d="M45.7,200C43.6,208.6,35.8,216.3,28.2,224.1c-7.6,7.8-15.1,15.8-19.8,25.5c-4.7,9.7-6.1,21.1-2.8,31.5
            c3.3,10.4,11.4,19.1,20.8,25.5c9.4,6.4,20.1,10.5,31.1,12.8c11,2.3,22.4,2.8,33.2,0.9c10.8-1.9,21-6.3,29.6-12.8
            c8.6-6.5,15.6-15.1,19.8-25.1c4.2-10,5.5-21.2,3.2-31.9c-2.3-10.7-8.2-20.3-16.5-27.8C118.5,214.9,107.9,209.6,97,206.4
            C86.1,203.2,74.8,202.1,63.8,203.2C52.8,204.3,42.2,207.6,32.9,213.1c-9.3,5.5-17.3,13.2-22.8,22.5c-5.5,9.3-8.3,20.1-8.3,30.9
            c0,10.8,2.8,21.6,8.3,30.9c5.5,9.3,13.5,17,22.8,22.5c9.3,5.5,19.9,8.8,30.9,9.9c11,1.1,22.3,0,33.2-3.2
            c10.9-3.2,21.5-8.5,29.8-15.9c8.3-7.4,14.2-17,16.5-27.8c2.3-10.7,1-21.9-3.2-31.9c-4.2-10-11.2-18.6-19.8-25.1
            c-8.6-6.5-18.8-10.9-29.6-12.8c-10.8-1.9-22.2-1.4-33.2,0.9c-11,2.3-21.7,6.4-31.1,12.8c-9.4,6.4-17.5,15.1-20.8,25.5
            c-3.3,10.4-1.9,21.8,2.8,31.5c4.7,9.7,12.2,17.7,19.8,25.5c7.6,7.8,15.4,15.5,17.5,24.1" />
        </svg>
      </div>

      <div className="container relative z-10 mx-auto px-6">
        <div ref={ref} className={cn("grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-8", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10", "transition-all duration-1000 ease-out")}>
          {features.map((feature, i) => <div key={i} data-index={i} className="group flex flex-col items-start space-y-6">
              {}
              <div className="relative">
                <div className="absolute -inset-4 rounded-full bg-primary/20 blur-xl transition-all duration-500 group-hover:bg-primary/30" />
                <feature.icon className="relative h-12 w-12 text-primary drop-shadow-[0_0_8px_rgba(var(--color-primary),0.6)]" strokeWidth={1.5} />
              </div>

              {}
              <div className="space-y-3">
                <h3 className="font-serif text-2xl font-medium tracking-wide text-foreground">
                  {feature.title}
                </h3>
                <p className="text-muted-foreground max-w-xs text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>

              {}
              <div className="h-px w-12 bg-gradient-to-r from-primary/50 to-transparent transition-all duration-500 group-hover:w-full group-hover:from-primary" />
            </div>)}
        </div>
      </div>
    </section>;
}